import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useCallback } from "react";
import {
  slackTriggerAvailabilityQueryOptions,
  slackTriggerChannelsQueryOptions,
} from "@/shared/slackTriggerQueries.ts";
import type {
  CreateRoutineReq,
  DigestConfigResp,
  EmailLabelConfigResp,
  ListRoutinesResp,
  RoutinePermissions,
  RoutineResp,
  RoutineSettingValues,
  RoutineThreadResp,
  UpdateDigestConfigReq,
  UpdateEmailLabelConfigReq,
  UpdateRoutineReq,
} from "../types/routines.ts";

export const routineQueries = {
  all: () => ["routines"] as const,
  list: () =>
    queryOptions({
      queryKey: [...routineQueries.all(), "list"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ListRoutinesResp>("/api/a/routines", { signal });
        return response.data;
      },
    }),
  detail: (routineId: string | undefined) =>
    queryOptions({
      queryKey: [...routineQueries.all(), routineId] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<RoutineResp>(`/api/a/routines/${routineId}`, { signal });
        return response.data;
      },
      enabled: !!routineId,
    }),
  threads: (routineId: string | undefined) =>
    queryOptions({
      queryKey: [...routineQueries.all(), routineId, "threads"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<RoutineThreadResp[]>(
          `/api/a/routines/${routineId}/threads`,
          {
            signal,
          },
        );
        return response.data;
      },
      enabled: !!routineId,
    }),
  labelConfig: (systemId: string | undefined) =>
    queryOptions({
      queryKey: [...routineQueries.all(), systemId, "label-config"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<EmailLabelConfigResp>(
          `/api/a/routines/system/${systemId}/label-config`,
          { signal },
        );
        return response.data;
      },
      enabled: !!systemId,
    }),
  digestConfig: (systemId: string | undefined) =>
    queryOptions({
      queryKey: [...routineQueries.all(), systemId, "digest-config"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<DigestConfigResp>(
          `/api/a/routines/system/${systemId}/digest-config`,
          { signal },
        );
        return response.data;
      },
      enabled: !!systemId,
    }),
  slackTriggerAvailability: slackTriggerAvailabilityQueryOptions,
  slackTriggerChannels: slackTriggerChannelsQueryOptions,
};

export const useDigestConfig = (systemId: string | undefined) => {
  const { data, isLoading, error } = useQuery(routineQueries.digestConfig(systemId));
  return { config: data, isLoading, error };
};

export const useLabelConfig = (systemId: string | undefined) => {
  const { data, isLoading, error } = useQuery(routineQueries.labelConfig(systemId));
  return { config: data, isLoading, error };
};

export const useSlackTriggerAvailability = () => {
  const { data } = useQuery(routineQueries.slackTriggerAvailability());
  return data;
};

export const useRoutines = () => {
  const { data, isLoading, error, refetch } = useQuery(routineQueries.list());
  return { routines: data ?? [], isLoading, error, refetch };
};

export const useRoutine = (routineId: string | undefined) => {
  const { data, isLoading, error } = useQuery(routineQueries.detail(routineId));
  return { routine: data, isLoading, error };
};

export const useRoutineThreads = (routineId: string | undefined) => {
  const { data, isLoading, error } = useQuery(routineQueries.threads(routineId));
  return { threads: data ?? [], isLoading, error };
};

export const useRoutineMutations = () => {
  const queryClient = useQueryClient();

  const createRoutine = useCallback(
    async (data: CreateRoutineReq) => {
      const response = await api.post<RoutineResp>("/api/a/routines", data);
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  const updateRoutine = useCallback(
    async (routineId: string, data: UpdateRoutineReq) => {
      const response = await api.post<RoutineResp>(`/api/a/routines/${routineId}`, data);
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  const deleteRoutine = useCallback(
    async (routineId: string) => {
      await api.post(`/api/a/routines/${routineId}/delete`);
      // Remove the deleted routine's queries so react-query doesn't refetch a 404 with retries
      queryClient.removeQueries({ queryKey: [...routineQueries.all(), routineId] });
      await queryClient.invalidateQueries({ queryKey: routineQueries.list().queryKey });
    },
    [queryClient],
  );

  const runRoutine = useCallback(async (routineId: string) => {
    await api.post(`/api/a/routines/${routineId}/run`);
  }, []);

  /**
   * Toggle a system routine's enabled state by writing to
   * `account.preferences.routines[<id>]`. Returns the system row with the
   * override applied.
   */
  const toggleSystemRoutine = useCallback(
    async (systemRoutineId: string, enabled: boolean) => {
      const response = await api.post<RoutineResp>(
        `/api/a/routines/system/${systemRoutineId}/toggle`,
        { enabled },
      );
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  /**
   * Edit a system routine. Every dimension writes a preferences override, so
   * the shipped definition stays authoritative and the user keeps tracking
   * upstream improvements. The prompt is okthx's and is ignored if sent —
   * `amendment` is the field that changes what the routine says.
   */
  const editSystemRoutine = useCallback(
    async (systemRoutineId: string, patch: UpdateRoutineReq) => {
      const response = await api.post<RoutineResp>(
        `/api/a/routines/system/${systemRoutineId}/edit`,
        patch,
      );
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  /**
   * Write per-user setting values for a system routine. Partial — only the
   * keys present are updated (instant per-change write-through). Stored as a
   * preferences override.
   */
  const updateSystemRoutineSettings = useCallback(
    async (systemRoutineId: string, settingValues: RoutineSettingValues) => {
      const response = await api.post<RoutineResp>(
        `/api/a/routines/system/${systemRoutineId}/settings`,
        { settingValues },
      );
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  /**
   * Write a per-user permissions override for a system routine. Stored as a
   * preferences override so the routine keeps tracking upstream
   * prompt/schedule updates. Sibling of `updateSystemRoutineSettings`.
   */
  const updateSystemRoutinePermissions = useCallback(
    async (systemRoutineId: string, permissions: RoutinePermissions) => {
      const response = await api.post<RoutineResp>(
        `/api/a/routines/system/${systemRoutineId}/permissions`,
        { permissions },
      );
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  /** Write a per-user cron schedule override for a system routine. */
  const updateSystemRoutineSchedule = useCallback(
    async (systemRoutineId: string, schedule: string) => {
      const response = await api.post<RoutineResp>(
        `/api/a/routines/system/${systemRoutineId}/schedule`,
        { schedule },
      );
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  /** Save the full email-label config (custom labels + built-in overrides + prefix). */
  const saveLabelConfig = useCallback(
    async (systemRoutineId: string, body: UpdateEmailLabelConfigReq) => {
      const response = await api.post<EmailLabelConfigResp>(
        `/api/a/routines/system/${systemRoutineId}/label-config`,
        body,
      );
      await queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      return response.data;
    },
    [queryClient],
  );

  /** Save the Auto Digest label selection (the ids of labels to summarize daily). */
  const saveDigestConfig = useCallback(
    async (systemRoutineId: string, body: UpdateDigestConfigReq) => {
      const response = await api.post<DigestConfigResp>(
        `/api/a/routines/system/${systemRoutineId}/digest-config`,
        body,
      );
      // Only the digest selection changed — refetch just that query rather than
      // the whole routines tree (list/detail/threads/label-config are unaffected).
      await queryClient.invalidateQueries({
        queryKey: routineQueries.digestConfig(systemRoutineId).queryKey,
      });
      return response.data;
    },
    [queryClient],
  );

  return {
    createRoutine,
    updateRoutine,
    deleteRoutine,
    runRoutine,
    toggleSystemRoutine,
    editSystemRoutine,
    updateSystemRoutineSettings,
    updateSystemRoutinePermissions,
    updateSystemRoutineSchedule,
    saveLabelConfig,
    saveDigestConfig,
  };
};
